import _get from 'lodash/get';
import { startAppListening } from '../../../state/middlewares/listener';
import { RootState, StopType } from '../../../types';
import * as appUtils from '../../../utils/basics';
import { GaClassicEvent, trackEvent } from '../../../utils/eventTracking';
import { getItemSetsVolume } from '../../../utils/itemSet';
import {
  getDeliveryFloorData,
  getDeliveryHelp,
  getDistance,
  getNotGuaranteedReason,
  getPickupFloorData,
  getPickupHelp,
  getStopDestinationDetails,
  getStopSituation,
  getValidItemSets,
} from '../ducks';
import { FLOW_TYPE } from '../index';
import { GeneralFlowActionTypes } from '../typings';

function progressTracking(action): void {
  const event = {
    event: 'Flow progress',
    flow_type: FLOW_TYPE,
  };
  let progressLabel = 'undefined';
  let progressValue = action.payload;
  switch (action.payload) {
    case 1:
      progressLabel = 'Step 1 - Destination';
      break;
    case 2:
      progressLabel = 'Step 2 - Type';
      break;
    case 3:
      progressLabel = 'Step 3 - Items';
      break;
    case 4:
      progressLabel = 'Step 4 - Date & Time';
      break;
    case 5:
      progressLabel = 'Step 5 - Services';
      break;
    case 6:
      progressLabel = 'Step 6 - Contact';
      break;
    case 7:
      progressLabel = 'Step 7 - Terms & Payment';
      break;
    /**
     * Let's see if this is still needed
     */
    case 'payment_not_guaranteed':
      progressLabel = 'Step 7 - Payment non guaranteed';
      progressValue = 7;
      break;
  }

  event['progress_label'] = progressLabel;
  event['progress_value'] = progressValue;
  appUtils.pushToDataLayer(event);
}

function distanceTracking(state: RootState): void {
  const distance = getDistance(state);
  // No quote yet → no distance to report (getDistance falls back to 0)
  if (!distance) {
    return;
  }
  const ranges = [10, 30, 50, 75, 100, 150, 200, 250, 300, 400];
  let action = '> 400 km';
  for (let i = 0; i < ranges.length; i++) {
    if (distance < ranges[i]) {
      action = ranges[i] === 10 ? '< ' : '';
      action += `${ranges[i]} km`;
      break;
    }
  }
  trackEvent({
    eventCategory: 'General TR - Distance',
    eventAction: action,
    eventLabel: String(distance),
    eventValue: distance,
  });
}

function metaDataTracking(state: RootState): void {
  const guaranteed = !!state.generalTransport.quote?.guaranteed.result;
  const notGuaranteedReason = getNotGuaranteedReason(state);
  const items = getValidItemSets(state);
  const pickupDetails = getStopDestinationDetails(state, StopType.PICKUP);
  const deliveryDetails = getStopDestinationDetails(state, StopType.DELIVERY);
  const pickupSituation = getStopSituation(state, StopType.PICKUP, false);
  const pickupHelp = getPickupHelp(state);
  const deliveryHelp = getDeliveryHelp(state);
  const pickupFloorData = getPickupFloorData(state);
  const deliveryFloorData = getDeliveryFloorData(state);
  const transportPrice = state.generalTransport.quote?.price.incl_vat.amount || 0;
  const auctionType = state.generalTransport.choices?.auction_type;

  // collect events
  const events: GaClassicEvent[] = [];

  // Guaranteed - Check if the price === 0, if not we have a valid guaranteed/not guaranteed answer
  if (transportPrice !== 0) {
    events.push({
      eventCategory: 'General TR - Guaranteed',
      eventAction: String(guaranteed),
      eventLabel: notGuaranteedReason,
    });
  }

  // Auction type
  if (auctionType) {
    events.push({
      eventCategory: 'General TR - Auction type',
      eventAction: auctionType,
    });
  }

  // Items
  if (items.length > 0) {
    events.push({
      eventCategory: 'General TR - volume m3',
      eventAction: Number(getItemSetsVolume(items) / 1000000).toFixed(2),
    });
    events.push({
      eventCategory: 'General TR - number of items',
      eventAction: String(items.length),
    });
  }

  // Region & country
  const pickupCountry = _get(pickupDetails, 'country_code', 'none');
  const deliveryCountry = _get(deliveryDetails, 'country_code', 'none');
  const pickupAdministrative = _get(pickupDetails, 'administrative_area', 'none');
  const deliveryAdministrative = _get(deliveryDetails, 'administrative_area', 'none');
  if (pickupDetails !== null) {
    events.push({
      eventCategory: 'General TR - Pickup region',
      eventAction: pickupAdministrative,
    });
    events.push({
      eventCategory: 'General TR - Pickup country',
      eventAction: pickupCountry,
    });
  }

  if (deliveryDetails !== null) {
    events.push({
      eventCategory: 'General TR - Delivery region',
      eventAction: deliveryAdministrative,
    });
    events.push({
      eventCategory: 'General TR - Delivery country',
      eventAction: deliveryCountry,
    });
  }

  if (pickupDetails !== null && deliveryDetails !== null) {
    events.push({
      eventCategory: 'General TR - Pickup => Delivery | region',
      eventAction: `From ${pickupAdministrative} to ${deliveryAdministrative}`,
      eventLabel: pickupAdministrative === deliveryAdministrative ? 'same' : 'different',
    });
    events.push({
      eventCategory: 'General TR - Pickup => Delivery | country',
      eventAction: `From ${pickupCountry} to ${deliveryCountry}`,
      eventLabel: pickupCountry === deliveryCountry ? 'same' : 'different',
    });
  }

  // Situation / type
  if (pickupSituation) {
    events.push({
      eventCategory: 'General TR - Type of transport (pickup situation)',
      eventAction: pickupSituation,
    });
  }

  // Help
  if (pickupHelp !== 'not_needed') {
    events.push({
      eventCategory: 'General TR - Pickup help',
      eventAction: pickupHelp,
    });
  }

  if (deliveryHelp !== 'not_needed') {
    events.push({
      eventCategory: 'General TR - Delivery help',
      eventAction: deliveryHelp,
    });
  }

  // Floor / Elevator
  if (pickupFloorData?.floor_level) {
    events.push({
      eventCategory: 'General TR - Pickup floor',
      eventAction: pickupFloorData?.elevator ? 'Elevator' : pickupFloorData?.floor_level.toString(),
    });
  }

  if (deliveryFloorData?.floor_level) {
    events.push({
      eventCategory: 'General TR - Delivery floor',
      eventAction: deliveryFloorData?.elevator ? 'Elevator' : deliveryFloorData?.floor_level.toString(),
    });
  }

  // Fire events
  events.map(event => trackEvent(event));
}

/**
 * Registers the flow tracking on SET_PROGRESS_STEP (same behavior as the former trackingSaga).
 * Each handler is synchronous, so the former takeLatest watchers are plain listeners, registered in the same order.
 */
export const startTrackingListeners = (): void => {
  /**
   * Pushes the flow progress step to the dataLayer (same behavior as the former progressTracking saga).
   */
  startAppListening({
    type: GeneralFlowActionTypes.SET_PROGRESS_STEP,
    effect: action => progressTracking(action),
  });

  /**
   * Tracks the transport meta data: guaranteed, auction type, items, regions, situation, help and floors
   * (same behavior as the former metaDataTracking saga).
   */
  startAppListening({
    type: GeneralFlowActionTypes.SET_PROGRESS_STEP,
    effect: (_action, api) => metaDataTracking(api.getState()),
  });

  /**
   * Tracks the quoted distance in buckets (same behavior as the former distanceTracking saga).
   */
  startAppListening({
    type: GeneralFlowActionTypes.SET_PROGRESS_STEP,
    effect: (_action, api) => distanceTracking(api.getState()),
  });
};
